import { type FC, memo, type ReactNode } from "react";
import { css, cva } from "@stubhub/broadway-foundation/css";
import { PERFORMER_CARD_WIDTH_IN_PX } from "src/website-apps/responsive/stubhub-performer/components/page-body/constants";
import { PerformerInfoCard } from "src/website-apps/responsive/stubhub-performer/components/performer-info-card";
import { BannerTitle } from "src/website-apps/responsive/stubhub-performer/components/portal/combined-header-banner/component-wrappers/BannerTitle";
import { GlassImageWrapper } from "src/website-apps/responsive/stubhub-performer/components/portal/combined-header-banner/GlassImageWrapper";
import {
  PortalColLeft,
  PortalColRight,
} from "src/website-apps/responsive/stubhub-performer/components/portal/PortalCol";
import { usePerformerPageModelData } from "src/website-apps/responsive/stubhub-performer/hooks/usePerformerPageModelData";
import { PerformerViewMode } from "src/website-apps/shared/constants/performer-view-mode";
import { useIsCalendarView } from "src/website-apps/shared/hooks/useIsCalendarView";
import { PerformerFeaturedSection } from "./PerformerFeaturedSection";
import { glassImageContainer, glassImageHeaderWrapperRecipe, residencyHeroOverlaySurface } from "./styles";

const eventListFiltersWrapperStyles = css({ marginBlockStart: "primitives.200", marginBlockEnd: "primitives.250" });
const mlbPlayoffsEventListFiltersWrapperStyles = css({
  marginBlockStart: "primitives.0",
  marginBlockEnd: "primitives.150",
});
const gaEventListFiltersWrapperStyles = css({ marginBlockStart: "primitives.100", marginBlockEnd: "primitives.0" });

const artistInfoColumn = cva({
  base: {
    maxWidth: "[1200px]",
    color: "text",
    display: "flex",
    marginInlineStart: "auto",
    marginInlineEnd: "auto",
    boxSizing: "content-box",
    overflowX: "hidden",
    overflowY: "hidden",
    paddingInline: "[36px]", // 36px is not on the token scale (32→40)
  },
  variants: {
    layout: {
      default: {},
      calendar: {
        alignItems: "flex-end",
        paddingBlockEnd: "primitives.250",
      },
      residency: {
        alignItems: "flex-end",
        paddingBlockEnd: "primitives.250",
        paddingInlineStart: "primitives.300",
      },
    },
  },
  defaultVariants: {
    layout: "default",
  },
});

interface Props {
  /** Header content to display above the banner */
  header: ReactNode;
}

/** This component is the combined `Header & Hero Banner.` */
export const DesktopHeaderBanner: FC<Props> = memo(({ header }) => {
  const model = usePerformerPageModelData();
  const { isOnCalendarView, calendarRenderMode } = useIsCalendarView();
  const isResidency = calendarRenderMode === PerformerViewMode.Residency;

  if (isResidency) {
    // Residency: full-width row (text left + image right). BannerTitle is suppressed —
    // PerformerFeaturedSection owns the entire banner content area.
    return (
      <div className={glassImageHeaderWrapperRecipe({ border: "residency" })}>
        {header}
        <div>
          <div className={artistInfoColumn({ layout: isResidency ? "residency" : "calendar" })}>
            <PerformerFeaturedSection />
          </div>
        </div>
        <div className={glassImageContainer} aria-hidden>
          <div className={residencyHeroOverlaySurface} />
        </div>
      </div>
    );
  }

  const isGaShoppingMode = model.performerGridSurface?.type === "ga-festival";
  const isMlbPlayoffs = model.performerGridSurface?.type === "mlb-playoffs";
  let filtersWrapperClassName = eventListFiltersWrapperStyles;
  if (isGaShoppingMode) filtersWrapperClassName = gaEventListFiltersWrapperStyles;
  else if (isMlbPlayoffs) filtersWrapperClassName = mlbPlayoffsEventListFiltersWrapperStyles;

  const performerCard: ReactNode = isOnCalendarView ? <PerformerInfoCard showOnlyImage /> : null;

  return (
    <GlassImageWrapper>
      {header}
      <div>
        <div
          className={artistInfoColumn({
            layout: isOnCalendarView ? "calendar" : "default",
          })}
        >
          <PortalColLeft increasedRightMargin flex="1 1 auto" overflow="hidden" boxSizing="border-box">
            <BannerTitle
              categoryName={model.categorySummary.categoryName ?? ""}
              categoryId={model.categorySummary.categoryId ?? -1}
              aggregateFavorites={model.categorySummary.aggregateFavorites ?? 0}
            />
            <div className={filtersWrapperClassName} />
          </PortalColLeft>
          <PortalColRight
            minWidth={PERFORMER_CARD_WIDTH_IN_PX}
            flex={`0 0 ${PERFORMER_CARD_WIDTH_IN_PX}px`}
            overflow="hidden"
            boxSizing="border-box"
          >
            {performerCard}
          </PortalColRight>
        </div>
      </div>
    </GlassImageWrapper>
  );
});
DesktopHeaderBanner.displayName = "DesktopHeaderBanner";
